/* PBB Modern — shared interaction + scroll-reveal for the homepage redesign.
   Loaded once globally from layout/theme.liquid (bottom of body, alongside theme.js).
   Previously inlined in snippets/pbb-modern-head.liquid, which only pbb-hero.liquid rendered.
   init() is idempotent: shopify:section:load fires repeatedly in the theme editor. */
(function(){
  // Kept byte-identical to the selector list in assets/pbb-modern.css. If you change one,
  // change the other in the same edit — the CSS hides these and the JS reveals them, so any
  // drift means elements that hide and never show (or show with no animation).
  var SEL = '.pbbx[data-pbb-reveal], .pbbx [data-pbb-reveal]';
  var SEL_PENDING = '.pbbx[data-pbb-reveal]:not(.is-in), .pbbx [data-pbb-reveal]:not(.is-in)';

  // NodeList.forEach is absent in older browsers; use one consistent iteration form.
  function each(list, fn){ Array.prototype.forEach.call(list, fn); }

  // The spotlight is a :hover::before effect that can never fire on touch, and each
  // pointermove does an unthrottled getBoundingClientRect plus two custom-property
  // writes. On touch these cards are exactly what sits under a finger while scrolling.
  var finePointer = !window.matchMedia || window.matchMedia('(hover:hover) and (pointer:fine)').matches;

  function bind(){
    if(!finePointer) return;
    each(document.querySelectorAll('.pbbx-spot-card'), function(el){
      if(el.__pbbBound) return; el.__pbbBound=true;
      el.addEventListener('pointermove',function(e){
        var r=el.getBoundingClientRect();
        el.style.setProperty('--mx',((e.clientX-r.left)/r.width*100)+'%');
        el.style.setProperty('--my',((e.clientY-r.top)/r.height*100)+'%');
      });
    });
  }

  var io = null;

  function pending(){ return document.querySelectorAll(SEL_PENDING); }

  /* ---- scroll/resize listeners are armed only while something is still pending ----
     Most pages (PDP, collection, cart, search) contain zero .pbbx elements. Leaving a
     document-wide querySelectorAll wired to every scroll frame there is permanent work
     for nothing, on a mostly low-end-Android audience. */
  var scrollArmed = false;
  function armScroll(on){
    if(on === scrollArmed) return;
    scrollArmed = on;
    if(on){
      window.addEventListener('scroll', onScroll, {passive:true});
      window.addEventListener('resize', onResize);
    }else{
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    }
  }
  function disarmIfDone(){ if(!pending().length) armScroll(false); }

  function reveal(){
    // Disconnect BEFORE the early return, so a theme-editor reload that leaves nothing
    // pending still drops observations of now-detached nodes.
    if(io) io.disconnect();
    var els = pending();
    if(!els.length){ armScroll(false); return; }
    if(!('IntersectionObserver' in window)){
      // No observer support: show everything rather than hiding content forever.
      each(els, function(el){ el.classList.add('is-in'); });
      armScroll(false);
      return;
    }
    if(!io){
      io = new IntersectionObserver(function(entries){
        each(entries, function(e){
          if(!e.isIntersecting) return;
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        });
        disarmIfDone();
      }, { rootMargin:'0px 0px -12% 0px', threshold:0.01 });
    }
    each(els, function(el){ io.observe(el); });
    armScroll(true);
  }

  /* sweep() is a BACKSTOP, not the reveal path. The observer owns normal scroll reveals.
     The -12% rootMargin means an element lying entirely within the last 12% of the viewport
     at maximum scroll can never intersect, and would stay invisible forever. Closing that
     requires a check that triggers at the viewport edge (top < innerHeight), which is a
     strictly looser condition than the observer's — so if this ran on every scroll frame it
     would always win the race and the observer would never reveal anything. Hence it runs
     only on load/resize/pageshow and at maximum scroll. */
  function sweep(){
    var els = pending(), vh = window.innerHeight, hit = [], i;
    // Two passes: read all geometry first, then write. Interleaving classList.add with
    // getBoundingClientRect forces a layout recalc per element — up to ~50 in one frame.
    for(i=0;i<els.length;i++){
      if(els[i].getBoundingClientRect().top < vh) hit.push(els[i]);
    }
    for(i=0;i<hit.length;i++){ hit[i].classList.add('is-in'); }
    if(hit.length) disarmIfDone();
  }

  function atMaxScroll(){
    var y = window.scrollY !== undefined ? window.scrollY : window.pageYOffset;
    return window.innerHeight + y >= document.documentElement.scrollHeight - 1;
  }

  var sweepQueued = false;
  function queueSweep(){
    if(sweepQueued) return;
    sweepQueued = true;
    // .call(window, …) because requestAnimationFrame/setTimeout require a window receiver:
    // an unbound reference works only in sloppy mode and would throw Illegal invocation
    // under 'use strict'. The finally is what stops one throw from latching sweepQueued
    // true forever and silently killing every future sweep.
    var raf = window.requestAnimationFrame || window.setTimeout;
    raf.call(window, function(){
      try{ sweep(); }
      finally{ sweepQueued = false; }
    });
  }

  function onScroll(){ if(atMaxScroll()) queueSweep(); }
  function onResize(){ queueSweep(); }

  // Fail open: drop the gate class so the CSS stops hiding anything. Mirrors the
  // watchdog in layout/theme.liquid. String-based so it works without classList.
  function failOpen(){
    var r=document.documentElement;
    r.className=r.className.replace(/(^|\s)pbb-js(\s|$)/,'$1');
  }

  function init(){
    // Why this try/catch exists: a throw in here would otherwise strand every revealed
    // element at opacity:0, and on every path that matters the watchdog cannot see it.
    // This file is a parser-inserted classic script at the end of <body>, so at execution
    // time document.readyState is 'loading' (measured, and guaranteed by the HTML parsing
    // spec) — the synchronous branch below is NOT taken. init() therefore runs at
    // DOMContentLoaded, which is after window.__pbbReveal is set at the bottom of this
    // IIFE, so the watchdog has already found the flag and no-opped. Same for the
    // shopify:section:load re-entry path, which fires arbitrarily later in the editor.
    // The one case the watchdog WOULD cover is the synchronous branch (readyState already
    // past 'loading', e.g. if an app injects this file late) — harmless overlap.
    try{ bind(); reveal(); sweep(); }
    catch(e){
      // Fail open, but leave a trace: the failure modes around here are otherwise silent.
      if(window.console && console.error) console.error('[pbb-modern] reveal init failed, showing all content:', e);
      failOpen();
    }
  }

  if(document.readyState!=='loading') init(); else document.addEventListener('DOMContentLoaded',init);
  document.addEventListener('shopify:section:load',init);

  // bfcache restore repaints without re-running init, so re-check then too.
  window.addEventListener('pageshow', sweep);
  window.addEventListener('load', function(){
    sweep();
    // Content injected into a .pbbx subtree after load (apps, lazy widgets) can land in
    // the viewport with no scroll, resize or section:load to catch it. Two cheap one-shots
    // close that without observing the subtree — a MutationObserver is not worth the cost
    // on this device class.
    setTimeout(sweep, 2000);
    setTimeout(sweep, 6000);
  });

  // LAST statement on purpose. This flag tells the watchdog in layout/theme.liquid that
  // this file parsed AND executed to completion AND the listeners above are armed.
  // Setting it at the top would not help with a 404, a parse error or a CSP block (none
  // of those execute any of this file), but it WOULD wrongly assert success if any of
  // the setup above threw. Last is the strictly stronger guarantee.
  window.__pbbReveal = true;
})();
